import { Plus, X } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldRow,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { DatePicker } from "@/components/DatePicker"
import { FormCard } from "@/components/FormCard"
import { TimeSelect } from "@/components/schedule/TimeSelect"
import { TIME_OPTIONS } from "@/lib/timeOptions"
import {
  emptyMember,
  type ActRequisitesInput,
  type CommissionMember,
} from "@/lib/actRequisites"

/**
 * warehouse.md's «Реквізити акта (форма № 244)» — ported field-for-field
 * from the wireframe's `ActRequisitesFields`/`TerritorialBodyField`. Every
 * label, placeholder, and the `length="4x"`/`controlLength="4x"` split
 * between plain-Input fields and `DatePicker` fields is copied verbatim
 * from the page spec's extracted source facts, not re-derived.
 */
export function ActRequisitesFields({
  onChange,
  value,
}: {
  value: ActRequisitesInput
  onChange: (value: ActRequisitesInput) => void
}) {
  function patch(next: Partial<ActRequisitesInput>) {
    onChange({ ...value, ...next })
  }

  function updateMember(index: number, next: Partial<CommissionMember>) {
    patch({
      members: value.members.map((member, i) =>
        i === index ? { ...member, ...next } : member
      ),
    })
  }

  function removeMember(index: number) {
    patch({ members: value.members.filter((_, i) => i !== index) })
  }

  return (
    <FieldGroup>
      <FieldRow>
        <Field controlLength="4x">
          <FieldLabel htmlFor="act-date">Дата складання</FieldLabel>
          <DatePicker
            id="act-date"
            onChange={(actDate) => patch({ actDate })}
            value={value.actDate}
          />
        </Field>
        <Field controlLength="4x">
          <FieldLabel htmlFor="act-from">Період: з</FieldLabel>
          <DatePicker
            id="act-from"
            onChange={(dateFrom) => patch({ dateFrom })}
            value={value.dateFrom}
          />
        </Field>
        <Field controlLength="4x">
          <FieldLabel htmlFor="act-to">Період: по</FieldLabel>
          <DatePicker
            id="act-to"
            onChange={(dateTo) => patch({ dateTo })}
            value={value.dateTo}
          />
        </Field>
      </FieldRow>

      <Field>
        <FieldLabel htmlFor="act-location">Місце складання акта</FieldLabel>
        <Input
          id="act-location"
          onChange={(e) => patch({ location: e.target.value })}
          placeholder="напр., м. Київ"
          value={value.location}
        />
      </Field>

      <FormCard title="Комісія">
        <FieldGroup>
          <FieldRow>
            <Field length="4x">
              <FieldLabel htmlFor="chair-role">Голова: посада</FieldLabel>
              <Input
                id="chair-role"
                onChange={(e) =>
                  patch({ chair: { ...value.chair, role: e.target.value } })
                }
                placeholder="напр., Директор"
                value={value.chair.role}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="chair-name">Голова: ПІБ</FieldLabel>
              <Input
                id="chair-name"
                onChange={(e) =>
                  patch({ chair: { ...value.chair, name: e.target.value } })
                }
                value={value.chair.name}
              />
            </Field>
          </FieldRow>

          {value.members.map((member, index) => {
            const removable = index >= 2
            return (
              <FieldRow key={`member-${index}`}>
                <Field length="4x">
                  <FieldLabel htmlFor={`member-role-${index}`}>
                    Член комісії {index + 1}: посада
                  </FieldLabel>
                  <Input
                    id={`member-role-${index}`}
                    onChange={(e) =>
                      updateMember(index, { role: e.target.value })
                    }
                    placeholder="напр., Головний інженер"
                    value={member.role}
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor={`member-name-${index}`}>
                    Член комісії {index + 1}: ПІБ
                  </FieldLabel>
                  <Input
                    id={`member-name-${index}`}
                    onChange={(e) =>
                      updateMember(index, { name: e.target.value })
                    }
                    value={member.name}
                  />
                </Field>
                {removable && (
                  <Button
                    aria-label={`Видалити члена комісії ${index + 1}`}
                    className="self-end"
                    onClick={() => removeMember(index)}
                    size="icon-sm"
                    type="button"
                    variant="ghost"
                  >
                    <X />
                  </Button>
                )}
              </FieldRow>
            )
          })}

          <Button
            className="w-fit"
            onClick={() => patch({ members: [...value.members, emptyMember()] })}
            size="sm"
            type="button"
            variant="secondary"
          >
            <Plus data-icon="inline-start" />
            Додати члена комісії
          </Button>
        </FieldGroup>
      </FormCard>

      <p className="text-sm font-medium">Місце, час і спосіб знищення</p>
      <FieldRow>
        <Field>
          <FieldLabel htmlFor="destr-place">Місце знищення</FieldLabel>
          <Input
            id="destr-place"
            onChange={(e) => patch({ destructionPlace: e.target.value })}
            placeholder="напр., м. Київ, вул. Прикладна, 1"
            value={value.destructionPlace}
          />
        </Field>
        <Field length="4x">
          <FieldLabel htmlFor="destr-time">Час знищення</FieldLabel>
          <TimeSelect
            id="destr-time"
            onValueChange={(destructionTime) => patch({ destructionTime })}
            options={TIME_OPTIONS}
            placeholder="Оберіть час"
            value={value.destructionTime}
          />
        </Field>
      </FieldRow>

      <Field>
        <FieldLabel htmlFor="destr-method">Спосіб знищення</FieldLabel>
        <Input
          id="destr-method"
          onChange={(e) => patch({ destructionMethod: e.target.value })}
          placeholder="напр., подрібнення шредером"
          value={value.destructionMethod}
        />
      </Field>
    </FieldGroup>
  )
}

/** warehouse.md's `TerritorialBodyField` — the copy-duty recipient, п. 12 № 146. */
export function TerritorialBodyField({
  onChange,
  value,
}: {
  value: string
  onChange: (value: string) => void
}) {
  return (
    <Field>
      <FieldLabel htmlFor="territorial-body">
        Територіальний орган МВС, який видав бланки
      </FieldLabel>
      <Input
        id="territorial-body"
        onChange={(e) => onChange(e.target.value)}
        placeholder="напр., ТСЦ 8041 ГСЦ МВС у м. Києві"
        value={value}
      />
      <FieldDescription>
        Копію складеного акта суб'єкт надсилає цьому органу (п. 12 Постанови
        КМУ № 146). АРМ каналу до нього не має — надсилання відбувається поза
        системою.
      </FieldDescription>
    </Field>
  )
}
